import { useState, useEffect } from "react";
import { PetAvatar } from "@/components/PetAvatar";
import { DoodleLightbulb, DoodleQuestion, DoodlePencil } from "@/components/Doodles";

const STATUS_TO_STEP = {
  processing: 0,
  checking_cache: 1,
  queued: 1,
  generating_script: 2,
  generating_audio: 3,
  completed: 4,
};

const STEP_NAMES = ['Processing', 'Checking cache', 'Writing story', 'Creating audio', 'Drawing slides'];

const PRIYA_THOUGHTS = [
  "Priya is finding the perfect analogy... 🤔",
  "Bunty is asking 'But why?' for you... 💭",
  "Priya is connecting ideas from your textbook... 📖",
  "Making sure you'll remember this forever... 🧠",
  "Almost ready — this is going to be fun!... 🎉",
  "Priya is choosing the best examples... ✨",
  "Simplifying the complex parts for you... 🎯",
];

const FUN_FACTS = [
  "A single tree produces enough oxygen for 2 people for a year!",
  "Your brain uses 20% of all the energy in your body!",
  "Honey never spoils — 3000-year-old honey was found edible!",
  "Octopuses have 3 hearts and blue blood!",
  "Lightning strikes Earth about 8 million times per day!",
  "The human nose can detect over 1 trillion different smells!",
  "Bananas are technically berries, but strawberries are not!",
  "A day on Venus is longer than a year on Venus!",
];

export const GenerationProgress = ({ status, topic }) => {
  const petType = localStorage.getItem('you_pet_type') || 'panda';
  const petName = localStorage.getItem('you_pet_name') || 'Your pet';

  const [thoughtIdx, setThoughtIdx] = useState(0);
  const [factIdx, setFactIdx]       = useState(0);

  useEffect(() => {
    const t = setInterval(() => setThoughtIdx(i => (i + 1) % PRIYA_THOUGHTS.length), 8000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setFactIdx(i => (i + 1) % FUN_FACTS.length), 10000);
    return () => clearInterval(t);
  }, []);

  const currentStep = STATUS_TO_STEP[status] ?? 0;

  return (
    <div
      data-testid="generation-progress"
      aria-live="polite"
      style={{ padding: 16, background: '#FFFFFF', position: 'relative' }}
    >
      <style>{`@keyframes gp-pulse{0%,100%{opacity:1}50%{opacity:0.25}}`}</style>

      {/* 1 — Pet Reading */}
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <PetAvatar size={72} state="thinking" petType={petType} />
        <p style={{ fontSize: 11, color: '#666666', marginTop: 4, marginBottom: 0 }}>
          {petName} is reading along...
        </p>
      </div>

      {/* 2 — Rotating Priya thought */}
      <div style={{
        background: '#FFFFFF', borderRadius: 4,
        padding: '12px 14px', marginBottom: 12, textAlign: 'center',
        border: '1.5px dashed #CCCCCC', borderLeft: '3px solid #448AFF', position: 'relative',
      }}>
        <div style={{ position: 'absolute', top: 4, right: 6, pointerEvents: 'none' }}>
          <DoodleQuestion color="#448AFF" opacity={0.2} size={12} />
        </div>
        <p style={{ fontSize: 15, fontFamily: "'Patrick Hand', cursive", color: '#4A4A5A', lineHeight: 1.5, margin: 0 }}>
          {PRIYA_THOUGHTS[thoughtIdx]}
        </p>
      </div>

      {/* 3 — Progress dots */}
      <div style={{ textAlign: 'center', marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 6 }}>
          {STEP_NAMES.map((_, i) => {
            const done    = i < currentStep;
            const current = i === currentStep;
            return (
              <div
                key={i}
                style={{
                  width: 12, height: 12, borderRadius: '50%', flexShrink: 0,
                  background: done ? '#448AFF' : 'transparent',
                  border: done ? '1.5px solid #448AFF' : current ? '2px dashed #448AFF' : '1.5px dashed #CCCCCC',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  animation: current ? 'gp-pulse 1.5s ease-in-out infinite' : 'none',
                }}
              >
                {done && <span style={{ fontSize: 14, color: '#fff', lineHeight: 1 }}>✓</span>}
              </div>
            );
          })}
        </div>
        <p style={{ fontSize: 11, color: '#666666', margin: 0 }}>
          Step {currentStep + 1} of 5 — {STEP_NAMES[currentStep]}
        </p>
      </div>

      {/* 4 — Did You Know */}
      <div style={{
        background: '#FFFFFF', borderRadius: 4, padding: '10px 14px',
        border: '1.5px dashed #CCCCCC', borderLeft: '3px solid #FFD740', marginBottom: 12, position: 'relative',
      }}>
        <div style={{ position: 'absolute', top: 4, right: 6, pointerEvents: 'none' }}>
          <DoodleLightbulb color="#FFD740" opacity={0.25} size={14} />
        </div>
        <p style={{ fontSize: 10, fontFamily: "'Patrick Hand', cursive", color: '#E6A800', fontWeight: 400, letterSpacing: 1, margin: '0 0 4px 0' }}>
          💡 DID YOU KNOW?
        </p>
        <p style={{ fontSize: 14, color: '#666666', lineHeight: 1.4, margin: 0 }}>
          {FUN_FACTS[factIdx]}
        </p>
      </div>

      {/* 5 — About This Topic */}
      {topic && (
        <div style={{
          background: '#FFFFFF', borderRadius: 4, padding: '10px 14px',
          border: '1.5px dashed #CCCCCC', borderLeft: '3px solid #66BB6A', marginBottom: 12,
        }}>
          <p style={{ fontSize: 10, fontFamily: "'Patrick Hand', cursive", color: '#2E7D32', fontWeight: 400, letterSpacing: 1, margin: '0 0 4px 0' }}>
            🌿 ABOUT THIS TOPIC
          </p>
          <p style={{ fontSize: 14, color: '#666666', lineHeight: 1.4, margin: 0 }}>
            You're about to learn about{' '}
            <strong style={{ color: '#4A4A5A' }}>
              {topic.slice(0, 40)}{topic.length > 40 ? '…' : ''}
            </strong>{' '}
            — one of the most fascinating concepts in your curriculum!
          </p>
        </div>
      )}

      {/* 6 — Pet Speech */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <PetAvatar size={48} state="happy" petType={petType} />
        <div style={{
          background: 'rgba(68,138,255,0.04)', borderRadius: 12, padding: '6px 10px', flex: 1,
          border: '1.5px dashed #448AFF',
        }}>
          <p style={{ fontSize: 11, color: '#666666', margin: 0 }}>
            I can't wait to learn this with you! Almost ready...
          </p>
        </div>
      </div>

      {/* 7 — Tags */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 6 }}>
        {[
          { label: '~60 seconds', color: '#448AFF', bg: 'rgba(68,138,255,0.08)'  },
          { label: '2 min story',  color: '#FFD740', bg: 'rgba(255,215,64,0.08)' },
          { label: '4 slides',     color: '#66BB6A', bg: 'rgba(102,187,106,0.08)'   },
        ].map(({ label, color, bg }) => (
          <span key={label} style={{
            fontSize: 11, color, background: 'transparent',
            border: `1px dashed ${color}`, fontFamily: "'Patrick Hand', cursive",
            borderRadius: 14, padding: '3px 8px', fontWeight: 400,
          }}>
            {label}
          </span>
        ))}
      </div>
    </div>
  );
};
